import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTA } from '../components/CTA';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Target, Users, Award, Lightbulb, ArrowRight } from 'lucide-react';
import { SEO, BreadcrumbSchema } from '../components/SEO';

export default function AboutPage() {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' }
  ];

  const values = [
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Everything we do is measured by one metric: the success of our contractor partners.',
    },
    {
      icon: Users,
      title: 'Partnership First',
      description: 'We see ourselves as an extension of your team, not just a vendor.',
    },
    {
      icon: Award,
      title: 'Quality Over Quantity',
      description: 'We\'d rather deliver 10 great appointments than 100 mediocre leads.',
    },
    {
      icon: Lightbulb,
      title: 'Continuous Innovation',
      description: 'We constantly refine our approach to stay ahead of market changes.',
    },
  ];

  const team = [
    {
      name: 'Leadership Team',
      description: 'Our leadership brings decades of combined experience in digital marketing, home services, and business development.',
    },
    {
      name: 'Marketing Specialists',
      description: 'Expert marketers who specialize in reaching homeowners actively seeking renovation and home improvement services.',
    },
    {
      name: 'Account Managers',
      description: 'Dedicated professionals who ensure every contractor partner receives personalized attention and support.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO 
        title="About RenoNovo - Your Growth Partner Since 2007"
        description="RenoNovo has been helping home service contractors grow since 2007. Learn about our mission to deliver exclusive, qualified appointments to pool builders, roofers, HVAC and remodeling contractors."
        keywords="about RenoNovo, contractor marketing company, home service marketing, appointment generation company, Satellite Beach FL"
        canonical="/about"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb]">
                  About RenoNovo
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0f172a] tracking-tight leading-none">
                  Your Growth Partner in Home Services
                </h1>
                <p className="text-lg text-[#64748b] leading-relaxed">
                  RenoNovo was founded with a simple mission: to help home service contractors 
                  grow their businesses without the frustration of traditional marketing. 
                  We believe contractors should focus on what they do best — delivering 
                  exceptional work — while we handle bringing qualified customers to their door.
                </p>
                <Link to="/contact">
                  <Button 
                    className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-8 h-14 rounded-sm text-base shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] hover:shadow-[0_6px_20px_0_rgba(37,99,235,0.5)] transition-all duration-200 hover:-translate-y-0.5 group"
                    data-testid="about-cta-btn"
                  >
                    Partner With Us
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Business partnership"
                  className="w-full h-auto rounded-sm shadow-[0_12px_24px_rgba(15,23,42,0.12)]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
                Our Story
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-6">
                Built by People Who Understand the Industry
              </h2>
              <p className="text-lg text-[#64748b] leading-relaxed">
                After years of watching talented contractors struggle with unreliable leads and 
                wasted marketing spend, we built RenoNovo to change the game. Our exclusive 
                territory model and pay-per-appointment pricing ensure that every dollar you 
                invest directly contributes to your growth.
              </p>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 border border-[#e2e8f0] rounded-sm">
                <p className="text-4xl font-bold text-[#2563eb] mb-2">2007</p>
                <p className="text-lg font-semibold text-[#0f172a] mb-2">Founded</p>
                <p className="text-[#64748b] text-sm">Started with a vision to transform contractor marketing</p>
              </div>
              <div className="text-center p-8 border border-[#e2e8f0] rounded-sm">
                <p className="text-4xl font-bold text-[#2563eb] mb-2">2021</p>
                <p className="text-lg font-semibold text-[#0f172a] mb-2">Nationwide Expansion</p>
                <p className="text-[#64748b] text-sm">Expanded to serve contractors across all 50 states</p>
              </div>
              <div className="text-center p-8 border border-[#e2e8f0] rounded-sm">
                <p className="text-4xl font-bold text-[#2563eb] mb-2">Today</p>
                <p className="text-lg font-semibold text-[#0f172a] mb-2">500+ Partners</p>
                <p className="text-[#64748b] text-sm">Trusted by contractors generating millions in revenue</p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 md:py-32 bg-[#f8fafc]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
                Our Values
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-6">
                What Drives Us
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div 
                  key={value.title}
                  className="bg-white p-8 border border-[#e2e8f0] rounded-sm hover:border-[#2563eb]/30 transition-all duration-300"
                  data-testid={`value-${index}`}
                >
                  <div className="w-14 h-14 bg-[#2563eb]/10 rounded-sm flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-[#2563eb]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0f172a] mb-3">{value.title}</h3>
                  <p className="text-[#64748b] text-sm leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <p className="text-sm tracking-wide uppercase font-semibold text-[#2563eb] mb-4">
                Our Team
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight mb-6">
                Experts Dedicated to Your Success
              </h2>
              <p className="text-lg text-[#64748b] max-w-2xl mx-auto">
                Our team combines deep industry expertise with cutting-edge marketing knowledge 
                to deliver results for our contractor partners.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((group, index) => (
                <div 
                  key={group.name}
                  className="text-center p-8 border border-[#e2e8f0] rounded-sm"
                  data-testid={`team-${index}`}
                >
                  <div className="w-20 h-20 bg-[#2563eb]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-10 h-10 text-[#2563eb]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0f172a] mb-3">{group.name}</h3>
                  <p className="text-[#64748b] leading-relaxed">{group.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
